import type { ApiSchemas } from '@provisium/web-api'

import { apiRequest } from './client'
import { demoRfqs } from '../dev/buyerDemoData'
import { demoUiDelay, isDemoUiEnabled, withDemoUi } from '../dev/demoMode'
import type { RfqDto, RfqLineDto } from './types'

// Тип провода берём из контракта: он генерируется из openapi.json, который печётся
// из кода бэкенда. Уберут или переименуют поле — здесь будет ошибка компиляции,
// а не тихий undefined в рантайме.
// camelCase-часть — совместимость со старым бэкендом (до раскатки snake в прод),
// снимается на шаге 4.
type RfqLineRaw = ApiSchemas['RfqLineDto'] & Partial<RfqLineDto>
type RfqRaw = ApiSchemas['RfqDto'] &
  Partial<RfqDto> & {
    supplier_contact?: RfqDto['supplierContact']
    lines?: RfqLineRaw[]
  }

const normalizeLine = (raw: RfqLineRaw): RfqLineDto => ({
  // Поля в контракте опциональные — типы из спеки это показали, обрабатываем undefined.
  id: raw.id ?? 0,
  productTypeId: raw.productTypeId ?? raw.product_type_id ?? 0,
  quantity: Number(raw.quantity ?? 0),
  unit: raw.unit ?? '',
  note: raw.note ?? null,
  sortOrder: raw.sortOrder ?? raw.sort_order ?? 0,
  supplierSku: raw.supplierSku ?? raw.supplier_sku ?? null,
  productName: raw.productName ?? raw.product_name ?? null,
  lineStatus: raw.lineStatus ?? raw.line_status ?? 'active',
})

export const normalizeRfq = (raw: RfqRaw): RfqDto => ({
  id: raw.id ?? 0,
  organizationId: raw.organizationId ?? raw.organization_id ?? 0,
  supplierOrganizationId: raw.supplierOrganizationId ?? raw.supplier_organization_id ?? 0,
  title: raw.title ?? '',
  displayNumber: raw.displayNumber ?? raw.display_number ?? null,
  batchNumber: raw.batchNumber ?? raw.batch_number ?? null,
  partIndex: raw.partIndex ?? raw.part_index ?? null,
  description: raw.description ?? null,
  status: raw.status ?? '',
  createdAt: raw.createdAt ?? raw.created_at ?? new Date(0).toISOString(),
  updatedAt: raw.updatedAt ?? raw.updated_at ?? new Date(0).toISOString(),
  supplierContact: raw.supplierContact ?? raw.supplier_contact ?? null,
  lines: (raw.lines ?? []).map(normalizeLine),
  desiredDeliveryDate: raw.desiredDeliveryDate ?? raw.desired_delivery_date ?? null,
  desiredDeliveryTimeUntil: raw.desiredDeliveryTimeUntil ?? raw.desired_delivery_time_until ?? null,
  deliveryAddress: raw.deliveryAddress ?? raw.delivery_address ?? null,
  supplierDecisionStatus: raw.supplierDecisionStatus ?? raw.supplier_decision_status ?? null,
  rejectionReasonCode: raw.rejectionReasonCode ?? raw.rejection_reason_code ?? null,
  substitutionComment: raw.substitutionComment ?? raw.substitution_comment ?? null,
  restaurantSubstitutionComment:
    raw.restaurantSubstitutionComment ?? raw.restaurant_substitution_comment ?? null,
  customerDisputeComment: raw.customerDisputeComment ?? raw.customer_dispute_comment ?? null,
})

export const listRestaurantRfqs = async (organizationId: number, venueId?: number | null) =>
  withDemoUi(demoRfqs, async () => {
  const params = new URLSearchParams({ organizationId: String(organizationId) })
  if (venueId != null) {
    params.set('venue_id', String(venueId))
  }
  const items = await apiRequest<RfqRaw[]>(`/rfqs?${params.toString()}`)
  return items.map(normalizeRfq)
})

export const getRestaurantRfq = async (rfqId: number) =>
  withDemoUi(
    demoRfqs.find((rfq) => rfq.id === rfqId) ?? demoRfqs[0],
    async () => {
  const rfq = await apiRequest<RfqRaw>(`/rfqs/${rfqId}`)
  return normalizeRfq(rfq)
})

/** Best-effort cancel: backend has no dedicated cancel; closes RFQ via status patch. */
export const closeRestaurantRfq = async (rfqId: number) => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    const rfq = demoRfqs.find((item) => item.id === rfqId) ?? demoRfqs[0]
    return { ...rfq, status: 'closed' as const }
  }
  const rfq = await apiRequest<RfqRaw>(`/rfqs/${rfqId}/status`, {
    method: 'PATCH',
    body: { status: 'closed' },
  })
  return normalizeRfq(rfq)
}

export const confirmRfqCompletion = (
  rfqId: number,
  action: 'confirm' | 'dispute',
  comment?: string,
) =>
  apiRequest<RfqRaw>(`/rfqs/${rfqId}/confirm-completion`, {
    method: 'PATCH',
    body: {
      action,
      comment: comment?.trim() || undefined,
    },
  }).then(normalizeRfq)

export const respondRfqSubstitution = (
  rfqId: number,
  action: 'accept' | 'exclude_lines' | 'cancel',
  options?: { excludedLineIds?: number[]; comment?: string },
) =>
  apiRequest<RfqRaw>(`/rfqs/${rfqId}/respond-substitution`, {
    method: 'PATCH',
    body: {
      action,
      excluded_line_ids: options?.excludedLineIds,
      comment: options?.comment?.trim() || undefined,
    },
  }).then(normalizeRfq)

export type CreateRfqLineRequest = {
  productTypeId: number
  quantity: number
  unit: string
  note?: string | null
}

export type CreateRfqRequest = {
  organizationId: number
  venueId?: number | null
  supplierOrganizationId: number
  title: string
  description?: string | null
  desiredDeliveryDate?: string | null
  desiredDeliveryTimeUntil?: string | null
  deliveryAddress?: string | null
  lines: CreateRfqLineRequest[]
}

export type CreateRfqBatchItemRequest = {
  supplierOrganizationId: number
  title: string
  lines: CreateRfqLineRequest[]
}

export type CreateRfqBatchRequest = {
  organizationId: number
  venueId?: number | null
  description?: string | null
  desiredDeliveryDate?: string | null
  desiredDeliveryTimeUntil?: string | null
  deliveryAddress?: string | null
  items: CreateRfqBatchItemRequest[]
}

export const createRfqBatch = (request: CreateRfqBatchRequest) =>
  apiRequest<RfqRaw[]>('/rfqs/batch', {
    method: 'POST',
    body: {
      organization_id: request.organizationId,
      ...(request.venueId != null ? { venue_id: request.venueId } : {}),
      description: request.description ?? undefined,
      desired_delivery_date: request.desiredDeliveryDate ?? undefined,
      desired_delivery_time_until: request.desiredDeliveryTimeUntil ?? undefined,
      delivery_address: request.deliveryAddress?.trim() || undefined,
      items: request.items.map((item) => ({
        supplier_organization_id: item.supplierOrganizationId,
        title: item.title,
        lines: item.lines.map((line) => ({
          product_type_id: line.productTypeId,
          quantity: line.quantity,
          unit: line.unit,
          note: line.note ?? undefined,
        })),
      })),
    },
  }).then((items) => items.map(normalizeRfq))

export const createRfq = (request: CreateRfqRequest) =>
  apiRequest<RfqRaw>('/rfqs', {
    method: 'POST',
    body: {
      organization_id: request.organizationId,
      ...(request.venueId != null ? { venue_id: request.venueId } : {}),
      supplier_organization_id: request.supplierOrganizationId,
      title: request.title,
      description: request.description ?? undefined,
      desired_delivery_date: request.desiredDeliveryDate ?? undefined,
      desired_delivery_time_until: request.desiredDeliveryTimeUntil ?? undefined,
      delivery_address: request.deliveryAddress?.trim() || undefined,
      lines: request.lines.map((line) => ({
        product_type_id: line.productTypeId,
        quantity: line.quantity,
        unit: line.unit,
        note: line.note ?? undefined,
      })),
    },
  }).then(normalizeRfq)
